/**
 * React Query hooks — Model Intelligence (Sprint 285 Stream G; REQ-MINT-012/013).
 *
 * Backend DR: `/api/v1/tenants/{tenant_id}/model-intel/*` (streams B–F).
 * La caché está **scoped por tenant** (igual que `useFrontier`): el aislamiento
 * es del backend, pero la UI no debe mezclar datos al cambiar de tenant.
 *
 * El **precio cliente lo calcula el backend** (`client_price` en `/models`);
 * la UI solo compone filas de presentación y nunca recibe coste ni margen.
 * Sin permiso de precio (`price_visibility="omitted"`) ⇒ «sin dato».
 */

import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useTenant } from "@/contexts/TenantContext";
import { composePricingRows } from "../lib/pricing";
import type {
  CompareRequest,
  CompareResponse,
  DetectionListResponse,
  ExportRequest,
  ExportResponse,
  FeedbackSummaryResponse,
  FunctionListResponse,
  ModelBenchmarkSummary,
  ModelCardResponse,
  ModelListResponse,
  ModelPricingComposition,
  ModelPricingListResponse,
  NewsListResponse,
  ObservabilityCell,
  ObservabilityResponse,
  OwnBenchListResponse,
  PlatformCostFactsResponse,
  PriceProposalListResponse,
  PriceVersionListResponse,
  SheetDetail,
  SourceListResponse,
} from "../types";

export const modelIntelKeys = {
  all: (tenantId?: string) => ["model-intel", tenantId] as const,
  composition: (tenantId?: string) =>
    [...modelIntelKeys.all(tenantId), "pricing-composition"] as const,
};

function basePath(tenantId: string): string {
  return `/api/v1/tenants/${tenantId}/model-intel`;
}

/** 403 curado del backend (`MINT_PRICE_FORBIDDEN` / denegación de módulo). */
export function isForbiddenError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return status === 403;
}

/** Tamaño de página del barrido de ofertas (el endpoint admite `limit<=200`). */
const MODELS_PAGE_SIZE = 200;

/**
 * Todas las ofertas del tenant, **paginadas por el `total` del contrato** (sin
 * topes fijos de cobertura): se recorre `offset` hasta `total` y solo se corta
 * si el backend devuelve una página vacía antes de tiempo (guarda de progreso,
 * no de tamaño).
 */
async function fetchModels(tenantId: string): Promise<ModelListResponse> {
  const { data: first } = await apiClient.get<ModelListResponse>(
    `${basePath(tenantId)}/models`,
    {
      params: { limit: MODELS_PAGE_SIZE, offset: 0 },
    },
  );
  const items = [...first.items];
  const total = Number.isFinite(first.total) ? first.total : items.length;
  while (items.length < total) {
    const { data: page } = await apiClient.get<ModelListResponse>(
      `${basePath(tenantId)}/models`,
      {
        params: { limit: MODELS_PAGE_SIZE, offset: items.length },
      },
    );
    if (page.items.length === 0) break;
    items.push(...page.items);
  }
  return { ...first, items };
}

/**
 * Composición de precios del tenant: `/models` ya trae el **precio cliente**
 * calculado en backend. Una sola entrada de caché por tenant (catálogo LLM,
 * vista BA y panel de Gobernanza comparten la misma consulta).
 */
export function useModelIntelPricingComposition() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: modelIntelKeys.composition(tenantId),
    queryFn: async (): Promise<ModelPricingComposition> => {
      const models = await fetchModels(tenantId!);
      return {
        contractVersion: models.contract_version,
        generatedAt: models.generated_at,
        priceVisibility: models.price_visibility,
        rows: composePricingRows(models),
      };
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/** Tamaños de página admitidos por el panel de precios (backend `limit<=200`). */
export const PRICING_PAGE_SIZES = [50, 100, 200] as const;
export type PricingPageSize = (typeof PRICING_PAGE_SIZES)[number];

export interface ModelIntelPricingListParams {
  /** Búsqueda server-side por nombre de modelo o proveedor. */
  q?: string;
  provider?: string;
  limit: PricingPageSize;
  offset: number;
}

/**
 * `GET /models/pricing` — listado ligero y **paginado en servidor** del panel
 * de precios BA. Solo devuelve ofertas con precio cliente vigente; los
 * benchmarks/telemetría de `/models` no se calculan ni viajan.
 */
export function useModelIntelPricingList(params: ModelIntelPricingListParams) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "pricing-list",
      params.q ?? "",
      params.provider ?? "",
      params.limit,
      params.offset,
    ] as const,
    queryFn: async (): Promise<ModelPricingListResponse> => {
      const { data } = await apiClient.get<ModelPricingListResponse>(
        `${basePath(tenantId!)}/models/pricing`,
        {
          params: {
            q: params.q || undefined,
            provider: params.provider || undefined,
            limit: params.limit,
            offset: params.offset,
          },
        },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
    // La página anterior se mantiene visible mientras llega la siguiente.
    placeholderData: keepPreviousData,
  });
}

/** `POST /compare` — alternativas elegibles con coste propio (si permiso). */
export function useModelIntelCompare() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (payload: CompareRequest): Promise<CompareResponse> => {
      if (!tenantId) throw new Error("No tenant selected");
      const { data } = await apiClient.post<CompareResponse>(
        `${basePath(tenantId)}/compare`,
        payload,
      );
      return data;
    },
  });
}

/** `POST /exports` — export auditado con cabecera de procedencia. */
export function useModelIntelExport() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (payload: ExportRequest): Promise<ExportResponse> => {
      if (!tenantId) throw new Error("No tenant selected");
      const { data } = await apiClient.post<ExportResponse>(
        `${basePath(tenantId)}/exports`,
        payload,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Lecturas del Panorama y el Comparador (Sprint 286 D)
// ---------------------------------------------------------------------------

/** `GET /models` — ofertas publicadas (Panorama/comparador base). */
export function useModelIntelModels() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "models"] as const,
    queryFn: () => fetchModels(tenantId!),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/** `GET /sources` — estado/frescura/veredicto de las fuentes de ingestión. */
export function useModelIntelSources() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "sources"] as const,
    queryFn: async (): Promise<SourceListResponse> => {
      const { data } = await apiClient.get<SourceListResponse>(
        `${basePath(tenantId!)}/sources`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/** `GET /functions` — funciones del producto para contrastar por función. */
export function useModelIntelFunctions() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "functions"] as const,
    queryFn: async (): Promise<FunctionListResponse> => {
      const { data } = await apiClient.get<FunctionListResponse>(
        `${basePath(tenantId!)}/functions`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Ficha y vista por función (Sprint 286 E)
// ---------------------------------------------------------------------------

/** `GET /models/{model_ref}` — ficha completa del modelo. */
export function useModelIntelModelCard(modelRef: string | null | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "card", modelRef] as const,
    queryFn: async (): Promise<ModelCardResponse> => {
      const { data } = await apiClient.get<ModelCardResponse>(
        `${basePath(tenantId!)}/models/${encodeURIComponent(modelRef!)}`,
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** `GET /benchmarks/models/{ref}` — serie histórica por benchmark (timeline). */
export function useModelIntelBenchmarkHistory(
  modelRef: string | null | undefined,
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "bench-history",
      modelRef,
    ] as const,
    queryFn: async (): Promise<ModelBenchmarkSummary> => {
      const { data } = await apiClient.get<ModelBenchmarkSummary>(
        `${basePath(tenantId!)}/benchmarks/models/${encodeURIComponent(modelRef!)}`,
      );
      return data;
    },
    enabled: !!tenantId && !!modelRef,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** `GET /sheets/{id}` — histórico de observaciones de una oferta (cambios). */
export function useModelIntelSheetHistory(
  sheetId: string | null | undefined,
  enabled = true,
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "sheet-history",
      sheetId,
    ] as const,
    queryFn: async (): Promise<SheetDetail> => {
      const { data } = await apiClient.get<SheetDetail>(
        `${basePath(tenantId!)}/sheets/${encodeURIComponent(sheetId!)}`,
      );
      return data;
    },
    enabled: !!tenantId && !!sheetId && enabled,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * Opciones replicadas de un modelo para el histórico de la ficha; 403 se
 * degrada a `null` («sin dato», nunca se infiere la tarifa omitida).
 */
/** `GET /observability` — telemetría propia por modelo (y función) desde rollups. */
export function useModelIntelObservability(
  modelRef: string | null | undefined,
  functionCode: string | null = null,
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "observability",
      modelRef,
      functionCode,
    ] as const,
    queryFn: async (): Promise<ObservabilityResponse> => {
      const { data } = await apiClient.get<ObservabilityResponse>(
        `${basePath(tenantId!)}/observability`,
        {
          params: {
            model_ref: modelRef,
            ...(functionCode ? { function_code: functionCode } : {}),
          },
        },
      );
      return data;
    },
    enabled: !!tenantId && !!modelRef,
    staleTime: 60 * 1000,
  });
}

/** Parámetros del panorama anónimo de uso (V-61; entrega 4). */
export interface UsageOverviewParams {
  periodType?: 'hour' | 'day' | 'week';
  usageMethod?: string;
  limit?: number;
}

/**
 * `GET /observability?source=cp` — agregados anónimos por forma de consumo.
 * Sin `model_ref` devuelve el ranking/mix de plataforma. Sin datos ⇒ lista
 * vacía; nunca se estima.
 */
export function useModelIntelUsageOverview(params: UsageOverviewParams = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      'usage-overview',
      params.periodType ?? 'day',
      params.usageMethod ?? '',
      params.limit ?? 200,
    ] as const,
    queryFn: async (): Promise<ObservabilityResponse> => {
      const { data } = await apiClient.get<ObservabilityResponse>(
        `${basePath(tenantId!)}/observability`,
        {
          params: {
            source: 'cp',
            period_type: params.periodType ?? 'day',
            limit: params.limit ?? 200,
            ...(params.usageMethod ? { usage_method: params.usageMethod } : {}),
          },
        },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/** Nº máximo de modelos a los que se pide uso en la vista por función. */
export const USAGE_FETCH_LIMIT = 12; /**
 * Uso real por función para un conjunto acotado de modelos (composición
 * client-side tolerante: los fallos se degradan a `null`, nunca rompen la
 * vista; sin celdas ⇒ «sin dato»).
 */
export function useModelIntelFunctionUsage(
  modelRefs: string[],
  functionCode: string | null,
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const refs = [...new Set(modelRefs)].sort().slice(0, USAGE_FETCH_LIMIT);

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "usage",
      functionCode,
      refs.join(","),
    ] as const,
    queryFn: async (): Promise<Record<string, ObservabilityCell[] | null>> => {
      const settled = await Promise.allSettled(
        refs.map(async (ref) => {
          const { data } = await apiClient.get<ObservabilityResponse>(
            `${basePath(tenantId!)}/observability`,
            {
              params: {
                model_ref: ref,
                ...(functionCode ? { function_code: functionCode } : {}),
              },
            },
          );
          return [ref, data.items] as const;
        }),
      );
      const out: Record<string, ObservabilityCell[] | null> = {};
      for (let index = 0; index < settled.length; index += 1) {
        const result = settled[index];
        if (result.status === "fulfilled")
          out[result.value[0]] = result.value[1];
        else out[refs[index]] = null;
      }
      return out;
    },
    enabled: !!tenantId && refs.length > 0,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Gobernanza: deriva de proveedor y timeline (Sprint 286 I)
// ---------------------------------------------------------------------------

/**
 * `GET /price-proposals` — deriva de proveedor de la región. Lectura explícita
 * de coste: sin permiso de precio el backend responde 403 (la UI degrada).
 */
export function useModelIntelPriceProposals(state: string | null = null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "price-proposals",
      state,
    ] as const,
    queryFn: async (): Promise<PriceProposalListResponse> => {
      const { data } = await apiClient.get<PriceProposalListResponse>(
        `${basePath(tenantId!)}/price-proposals`,
        { params: { ...(state ? { state } : {}), limit: 200 } },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * `GET /detections` — cola de curación de modelos nuevos (permiso `#edit`).
 * Sin permiso el backend responde 403 y la UI degrada a «sin dato».
 */
export function useModelIntelDetections(state: string | null = "pending") {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "detections", state] as const,
    queryFn: async (): Promise<DetectionListResponse> => {
      const { data } = await apiClient.get<DetectionListResponse>(
        `${basePath(tenantId!)}/detections`,
        { params: { ...(state ? { state } : {}), limit: 200 } },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** `GET /price-versions` — línea temporal de versiones aplicadas. */
export function useModelIntelPriceVersions(modelRef: string | null = null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "price-versions",
      modelRef,
    ] as const,
    queryFn: async (): Promise<PriceVersionListResponse> => {
      const { data } = await apiClient.get<PriceVersionListResponse>(
        `${basePath(tenantId!)}/price-versions`,
        {
          params: { ...(modelRef ? { model_ref: modelRef } : {}), limit: 200 },
        },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * Hechos de coste agregados por DR (consola M2M del CP; vista de plataforma).
 * El CP puede no estar enroutado: la página degrada a «sin dato» por región.
 */
export function useModelIntelPlatformCostFacts() {
  return useQuery({
    queryKey: ["model-intel", "platform", "cost-facts"] as const,
    queryFn: async (): Promise<PlatformCostFactsResponse> => {
      const { data } = await apiClient.get<PlatformCostFactsResponse>(
        "/api/admin/provider-costs/dr/cost-facts",
      );
      return data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * `GET /feedback/summary` — agregados k-anónimos (celdas bajo k suprimidas).
 * El crudo no se consume desde la UI; esta lectura sólo trae el agregado.
 */
export function useModelIntelFeedbackSummary(functionCode: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      ...modelIntelKeys.all(tenantId),
      "feedback-summary",
      functionCode,
    ] as const,
    queryFn: async (): Promise<FeedbackSummaryResponse> => {
      const { data } = await apiClient.get<FeedbackSummaryResponse>(
        `${basePath(tenantId!)}/feedback/summary`,
        {
          params: {
            ...(functionCode ? { function_code: functionCode } : {}),
            window_days: 30,
          },
        },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** `GET /news` — noticias publicadas recientes con cita (286-H). */
export function useModelIntelNews(windowDays: number = 30) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "news", windowDays] as const,
    queryFn: async (): Promise<NewsListResponse> => {
      const { data } = await apiClient.get<NewsListResponse>(
        `${basePath(tenantId!)}/news`,
        {
          params: { window_days: windowDays, limit: 20 },
        },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * `GET /benchmarks/own` — mediciones propias (F1; ADR-134). Solo agregados
 * (score, muestra, IC); los ítems de datasets gated nunca llegan a la UI.
 */
export function useModelIntelOwnBenchmarks(modelRef?: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...modelIntelKeys.all(tenantId), "own-bench", modelRef] as const,
    queryFn: async (): Promise<OwnBenchListResponse> => {
      const { data } = await apiClient.get<OwnBenchListResponse>(
        `${basePath(tenantId!)}/benchmarks/own`,
        { params: { ...(modelRef ? { model_ref: modelRef } : {}), limit: 50 } },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 60 * 1000,
  });
}
